// atoms.tsx — shared visual atoms for the Add New Product (V6) screen.
// Ported from docs/design/design_handoff_add_new_product/newproduct-data.jsx
// and mesh-data.jsx (glyphs). Pixel values are design-final; do not restyle.
import React from "react";

export const NP_DASH = "—";

const ICON_PATHS: Record<string, string[]> = {
    check: ["M4 12.5l5 5L20 6.5"],
    x: ["M6 6l12 12", "M18 6L6 18"],
    sparkle: [
        "M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z",
        "M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9z"
    ],
    plus: ["M12 5v14", "M5 12h14"],
    users: [
        "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2",
        "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
        "M22 21v-2a4 4 0 0 0-3-3.87",
        "M16 3.13a4 4 0 0 1 0 7.75"
    ],
    team: [
        "M18 21a8 8 0 0 0-16 0",
        "M10 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10z",
        "M22 20c0-3.4-2-6.3-5-7.5"
    ],
    flag: ["M4 22V4c0-.6.4-1 1-1h11.6a1 1 0 0 1 .9 1.5L16 8l1.5 3.5a1 1 0 0 1-.9 1.5H4"],
    eraser: [
        "M7 21l-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21",
        "M22 21H7",
        "M5 11l9 9"
    ]
};

export function NPIcon({
    name,
    size = 16,
    sw = 1.8,
    color = "currentColor"
}: {
    name: keyof typeof ICON_PATHS | string;
    size?: number;
    sw?: number;
    color?: string;
}) {
    const p = ICON_PATHS[name];
    if (!p) return null;
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            {p.map((d, i) => (
                <path key={i} d={d} />
            ))}
        </svg>
    );
}

export function NPGlyph({ name, size = 18 }: { name: string; size?: number }) {
    const s = size;
    switch (name) {
        case "jira":
            return (
                <svg width={s} height={s} viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M11.3 2.2 3 10.5l3.4 3.4 4.9-4.9-3.4-3.4z" fill="#2684FF" />
                    <path d="M12.7 9.1 8 13.8l4.7 4.7 1.6 1.6 4.9-4.9-6.5-6.1z" fill="#2684FF" fillOpacity="0.55" />
                </svg>
            );
        case "azure":
            return (
                <svg width={s} height={s} viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M4 18.5 10.2 8l3.6 4.6L6.8 18.5z" fill="#0078D4" />
                    <path d="M10.2 8 13 3l7 15.5-5-3.6L10.2 8z" fill="#0078D4" fillOpacity="0.55" />
                </svg>
            );
        case "files":
            return (
                <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.6" aria-hidden>
                    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" fill="rgba(99,102,241,0.14)" />
                    <path d="M14 3v5h5" />
                    <path d="M9 13h6M9 16.5h4" />
                </svg>
            );
        case "github":
            return (
                <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
                    <circle cx="12" cy="12" r="10" fill="#24292F" />
                    <path
                        d="M12 5.5c-3.7 0-6.6 3-6.6 6.7 0 3 1.9 5.5 4.5 6.4.3.1.5-.1.5-.3v-1.2c-1.9.4-2.3-.9-2.3-.9-.3-.8-.7-1-.7-1-.6-.4 0-.4 0-.4.7 0 1 .7 1 .7.6 1 1.5.7 1.9.5.1-.4.2-.7.4-.9-1.5-.2-3-.7-3-3.3 0-.7.3-1.3.7-1.8-.1-.2-.3-.9.1-1.8 0 0 .6-.2 1.8.7a6.3 6.3 0 0 1 3.4 0c1.2-.9 1.8-.7 1.8-.7.4.9.2 1.6.1 1.8.4.5.7 1.1.7 1.8 0 2.6-1.6 3.1-3 3.3.2.2.4.6.4 1.2v1.8c0 .2.2.4.5.3a6.7 6.7 0 0 0 4.5-6.4c0-3.7-3-6.7-6.7-6.7z"
                        fill="#fff"
                    />
                </svg>
            );
        case "gitlab":
            return (
                <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
                    <path
                        d="M12 21.5 3.7 15.4c-.6-.4-.8-1.2-.6-1.9l1.6-5 2.2-4.9c.2-.5.9-.5 1.1 0L10 8.2h4l2-4.6c.2-.5.9-.5 1.1 0l2.2 4.9 1.6 5c.2.7 0 1.5-.6 1.9z"
                        fill="#FC6D26"
                    />
                    <path d="m12 21.5-2-6.3h4z" fill="#E24329" />
                </svg>
            );
        case "bitbucket":
            return (
                <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
                    <path
                        d="M3.3 3.9 6 19.4c.1.6.6 1 1.2 1h9.7c.5 0 .9-.3 1-.8l2.8-15.7a.8.8 0 0 0-.8-.9H4a.8.8 0 0 0-.7.9z"
                        fill="#2684FF"
                    />
                    <path d="M14.6 9.4H9.4l.9 5.2h3.5z" fill="#fff" />
                </svg>
            );
        default:
            return null;
    }
}

export function RequiredBadge() {
    return (
        <span
            style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#4f46e5",
                background: "rgba(99,102,241,0.10)",
                padding: "3px 8px",
                borderRadius: 999
            }}
        >
            Required
        </span>
    );
}

export function OptionalBadge({ children = "Optional" }: { children?: React.ReactNode }) {
    return (
        <span
            style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--fg4)",
                background: "var(--bg-input)",
                padding: "3px 8px",
                borderRadius: 999
            }}
        >
            {children}
        </span>
    );
}

const BTN_TONES: Record<string, React.CSSProperties> = {
    ghost: { background: "transparent", border: "1px solid var(--divider)", color: "var(--fg2)" },
    primary: {
        background: "var(--np-brand)",
        border: "none",
        color: "#fff",
        boxShadow: "0 2px 8px rgba(39,51,194,0.3)"
    },
    soft: { background: "#fff", border: "1px solid var(--np-brand)", color: "var(--np-brand)" }
};

export function NPButton({
    children,
    tone = "ghost",
    small,
    disabled,
    onClick,
    title,
    style,
    uiBridge
}: {
    children: React.ReactNode;
    tone?: "ghost" | "primary" | "soft";
    small?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    title?: string;
    style?: React.CSSProperties;
    /** Registers the button with the FDE host-UI adapter (data-ui-bridge
     *  attributes). The adapter mirrors the DOM disabled state live, so the
     *  agent sees enabled/disabled exactly as the user does. */
    uiBridge?: { id: string; label?: string; description?: string; group?: string };
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            data-ui-bridge={uiBridge?.id}
            data-ui-label={uiBridge?.label}
            data-ui-description={uiBridge?.description}
            data-ui-group={uiBridge?.group}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                height: small ? 30 : 36,
                padding: small ? "0 12px" : "0 16px",
                borderRadius: 9,
                fontSize: small ? 12 : 13,
                fontWeight: 600,
                cursor: disabled ? "default" : "pointer",
                fontFamily: "inherit",
                opacity: disabled ? 0.55 : 1,
                ...BTN_TONES[tone],
                ...style
            }}
        >
            {children}
        </button>
    );
}

export function ClearChip({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={`Clear ${label} and start over`}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                height: 20,
                padding: "0 7px",
                borderRadius: 6,
                border: "1px solid var(--divider)",
                background: "transparent",
                cursor: "pointer",
                fontFamily: "inherit"
            }}
        >
            <NPIcon name="eraser" size={11} sw={1.9} color="var(--fg4)" />
            <span style={{ fontSize: 10.5, fontWeight: 600, color: "var(--fg4)" }}>Clear</span>
        </button>
    );
}
